import React, { useState, useEffect, useRef } from 'react';
import { 
  Play, 
  Users, 
  BarChart3, 
  Shield, 
  Zap, 
  CheckCircle, 
  ArrowRight,
  Calendar,
  CreditCard,
  Smartphone,
  Award,
  Target,
  Headphones,
  Clock,
  Star,
  Building,
  Printer,
  ShoppingCart,
  RefreshCw,
  Settings,
  ArrowLeft,
  ChevronRight,
  HelpCircle,
  Send,
  Timer,
  Gift,
  Loader2,
  Check,
  X,
  ChevronDown,
  Sparkles,
  Lock,
  Plus,
  MessageCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { AnimatedBackground } from './AnimatedBackground';

interface CreateEventsPageProps {
  onNavigate?: (page: string) => void;
}

// Logo container component with fallback system
function LogoContainer({ partner }: { partner: { name: string; logo: string; fallback?: string[] } }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFallback, setShowFallback] = useState(false);
  
  const allImages = [partner.logo, ...(partner.fallback || [])];
  
  const handleImageError = () => {
    if (currentImageIndex < allImages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    } else {
      setShowFallback(true);
    }
  };

  if (showFallback) {
    return (
      <div 
        className="flex items-center justify-center bg-white/50 dark:bg-gray-800/30 rounded-lg backdrop-blur-sm border border-border/20 group cursor-pointer transition-all duration-300 hover:bg-white/70 dark:hover:bg-gray-800/50"
        style={{
          width: '120px',
          height: '80px',
          minWidth: '120px',
          minHeight: '80px'
        }}
      >
        <div className="text-center px-2">
          <div className="text-xs font-semibold text-muted-foreground group-hover:text-foreground transition-colors">
            {partner.name}
          </div>
          <div className="text-[10px] text-muted-foreground/70 mt-1">
            Integrado
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="flex items-center justify-center bg-white/50 dark:bg-gray-800/30 rounded-lg backdrop-blur-sm border border-border/20 group cursor-pointer transition-all duration-300 hover:bg-white/70 dark:hover:bg-gray-800/50"
      style={{
        width: '120px',
        height: '80px',
        minWidth: '120px',
        minHeight: '80px'
      }}
    >
      <img 
        src={allImages[currentImageIndex]} 
        alt={partner.name}
        className="max-w-[90px] max-h-[50px] w-auto h-auto object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
        onError={handleImageError}
      />
    </div>
  );
}

// Enhanced Form Field Component with validation
function FormField({ 
  label, 
  name, 
  type = 'text', 
  value, 
  onChange, 
  placeholder, 
  required = false, 
  tooltip,
  validation 
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  tooltip?: string;
  validation?: { isValid: boolean; message: string };
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Label htmlFor={name} className="text-sm font-medium flex items-center">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle size={14} className="text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="relative">
        <Input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            setHasBeenTouched(true);
          }}
          placeholder={placeholder}
          className={`
            transition-all duration-300 
            ${isFocused ? 'ring-2 ring-orange-primary/20 border-orange-primary' : ''}
            ${hasBeenTouched && validation?.isValid === false ? 'border-red-500' : ''}
            ${hasBeenTouched && validation?.isValid === true ? 'border-green-500' : ''}
          `}
          required={required}
        />
        {hasBeenTouched && validation?.isValid === true && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Check size={16} className="text-green-500" />
          </div>
        )}
        {hasBeenTouched && validation?.isValid === false && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <X size={16} className="text-red-500" />
          </div>
        )}
      </div>
      {hasBeenTouched && validation && !validation.isValid && (
        <p className="text-sm text-red-500 flex items-center">
          <X size={14} className="mr-1" />
          {validation.message}
        </p>
      )}
    </div>
  );
}

// Scroll Progress Component
function ScrollProgress() {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollPx = document.documentElement.scrollTop;
      const winHeightPx = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (scrollPx / winHeightPx) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', updateScrollProgress);
    return () => window.removeEventListener('scroll', updateScrollProgress);
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-muted/30 z-50">
      <div 
        className="h-full bg-gradient-to-r from-orange-primary to-orange-primary/80 transition-all duration-150 ease-out"
        style={{ width: `${scrollProgress}%` }}
      />
    </div>
  );
}

// Floating CTA Component
function FloatingCTA({ onScroll }: { onScroll: () => void }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 1000) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40">
      <Button
        onClick={() => window.open(`${import.meta.env.VITE_REDIRECT_URL}/cadastro?createEvent=true`, '_blank')}
        size="lg"
        className="bg-gradient-to-r from-orange-primary to-orange-primary/80 hover:from-orange-primary/90 hover:to-orange-primary/70 text-white font-semibold shadow-2xl transition-all duration-300 hover:scale-105 rounded-full px-6 py-3"
      >
        <Plus size={20} className="mr-2" />
        Cadastrar evento
        <Sparkles size={16} className="ml-2 animate-pulse" />
      </Button>
    </div>
  );
}

export function CreateEventsPage({ onNavigate }: CreateEventsPageProps) {
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [isInView, setIsInView] = useState<{ [key: string]: boolean }>({});
  const formRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    eventType: '',
    expectedAttendees: '',
    acceptTerms: false
  });
  
  const [formValidation, setFormValidation] = useState<{[key: string]: { isValid: boolean; message: string }}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Enhanced scroll effects
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setIsInView(prev => ({
          ...prev,
          [entry.target.id]: entry.isIntersecting
        }));
      });
    }, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Form validation
  const validateField = (name: string, value: string) => {
    switch (name) {
      case 'name':
        return {
          isValid: value.length >= 2,
          message: 'Nome deve ter pelo menos 2 caracteres'
        };
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return {
          isValid: emailRegex.test(value),
          message: 'Digite um email válido'
        };
      case 'phone':
        const phoneRegex = /^[\d\s\(\)\-\+]{10,}$/;
        return {
          isValid: phoneRegex.test(value.replace(/\s/g, '')),
          message: 'Digite um telefone válido'
        };
      case 'company':
        return {
          isValid: value.length >= 2,
          message: 'Nome da empresa deve ter pelo menos 2 caracteres'
        };
      default:
        return { isValid: true, message: '' };
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Real-time validation
    const validation = validateField(name, value);
    setFormValidation(prev => ({ ...prev, [name]: validation }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setShowSuccess(true);
    
    setTimeout(() => {
      setShowSuccess(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        eventType: '',
        expectedAttendees: '',
        acceptTerms: false
      });
      setFormValidation({});
    }, 3000);
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const redirectToRegistration = () => {
    window.open(`${import.meta.env.VITE_REDIRECT_URL}/cadastro?createEvent=true`, '_blank');
  };

  const redirectToWhatsApp = () => {
    window.open('https://api.whatsapp.com/send?l=pt&phone=5582996064988', '_blank');
  };

  const isFormValid = formData.name && formData.email && formData.phone && formData.company && formData.acceptTerms;

  const partnerLogos = [
    { 
      name: 'Visa', 
      logo: 'https://cdn.worldvectorlogo.com/logos/visa-2.svg',
      fallback: ['https://logos-world.net/wp-content/uploads/2020/06/Visa-Logo.png']
    },
    { 
      name: 'Mastercard', 
      logo: 'https://logos-world.net/wp-content/uploads/2020/09/Mastercard-Logo.png',
      fallback: ['https://cdn.worldvectorlogo.com/logos/mastercard-2.svg']
    },
    { 
      name: 'American Express', 
      logo: 'https://cdn.worldvectorlogo.com/logos/amex-3.svg',
      fallback: ['https://logos-world.net/wp-content/uploads/2020/04/American-Express-Logo.png']
    },
    { 
      name: 'Pix', 
      logo: 'https://cdn.worldvectorlogo.com/logos/pix-2.svg',
      fallback: ['https://upload.wikimedia.org/wikipedia/commons/thumb/8/8b/Pix_logo.svg/200px-Pix_logo.svg.png', 'https://seeklogo.com/images/P/pix-logo-CA91C0B524-seeklogo.com.png']
    },
    { 
      name: 'Pagbank', 
      logo: 'https://cdn.worldvectorlogo.com/logos/pagbank-2.svg',
      fallback: ['https://seeklogo.com/images/P/pagseguro-logo-7B3B3F9E1E-seeklogo.com.png', 'https://logoeps.com/wp-content/uploads/2013/03/pagseguro-vector-logo.png']
    },
    { 
      name: 'PayPal', 
      logo: 'https://cdn.worldvectorlogo.com/logos/paypal-3.svg',
      fallback: ['https://logos-world.net/wp-content/uploads/2020/07/PayPal-Logo.png', 'https://seeklogo.com/images/P/paypal-logo-C7D8E21F9B-seeklogo.com.png']
    },
    { 
      name: 'Stripe', 
      logo: 'https://cdn.worldvectorlogo.com/logos/stripe-4.svg',
      fallback: ['https://logos-world.net/wp-content/uploads/2021/03/Stripe-Logo.png']
    },
    { 
      name: 'Apple Pay', 
      logo: 'https://cdn.worldvectorlogo.com/logos/apple-pay-2.svg',
      fallback: ['https://logos-world.net/wp-content/uploads/2020/04/Apple-Pay-Logo.png']
    },
    { 
      name: 'Google Pay', 
      logo: 'https://cdn.worldvectorlogo.com/logos/google-pay-2.svg',
      fallback: ['https://logos-world.net/wp-content/uploads/2020/09/Google-Pay-Logo.png']
    },
    { 
      name: 'PicPay', 
      logo: 'https://cdn.worldvectorlogo.com/logos/picpay-1.svg',
      fallback: ['https://logoeps.com/wp-content/uploads/2018/05/picpay-vector-logo.png']
    }
  ];

  const features = [
    {
      icon: CreditCard,
      title: 'Venda de Ingressos',
      description: 'Plataforma multi-canal com tecnologia de conversão e antifraude inteligente',
      details: [
        'Checkout otimizado com tecnologia de conversão avançada',
        'Sistema antifraude com inteligência artificial avançada',
        'Venda direta na maquininha com estoque unificado',
        'Vendas estratégicas: online, presencial e por parceiros'
      ]
    },
    {
      icon: BarChart3,
      title: 'Inteligência & Analytics',
      description: 'Dashboard inteligente em tempo real com insights estratégicos para tomada de decisão',
      details: [
        'Relatórios de performance em tempo real',
        'Perfil detalhado e inteligente do seu público',
        'Análise exclusiva de conversão e abandono',
        'Previsões estratégicas e projeções de vendas'
      ]
    },
    {
      icon: Smartphone,
      title: 'Controle de Acesso',
      description: 'Sistema completo e robusto de check-in e gestão de entrada com tecnologia QR',
      details: [
        'QR Code dinâmico e seguro de última geração',
        'App nativo avançado para validação',
        'Integração inteligente com catracas',
        'Controle de lotação em tempo real'
      ]
    },
    {
      icon: Users,
      title: 'Gestão de Equipe',
      description: 'Ferramentas completas e estratégicas para gestão de produtores, comissários e equipe',
      details: [
        'Sistema de comissionamento automático e inteligente',
        'Gestão personalizada de permissões por usuário',
        'Relatórios exclusivos de performance individual',
        'Controle estratégico de metas e incentivos'
      ]
    }
  ];

  const physicalSalesBenefits = [
    {
      icon: Zap,
      title: 'Venda direto na maquininha',
      description: 'Venda e imprima ingressos diretamente da maquininha com tecnologia avançada, sem necessidade de computador ou impressora externa. Estoque unificado entre online e físico em tempo real.'
    },
    {
      icon: Shield,
      title: 'Antifraude com IA',
      description: 'Sistema inteligente de detecção de fraudes com IA que analisa padrões de compra suspeitos e protege automaticamente seu evento contra tentativas fraudulentas.'
    },
    {
      icon: BarChart3,
      title: 'Conversão otimizada',
      description: 'Tecnologia de conversão que reduz abandono de carrinho e aumenta vendas com checkout inteligente, ofertas personalizadas e processo de compra simplificado.'
    }
  ];

  const benefits = [
    'Implementação rápida em até 48h',
    'Suporte técnico dedicado e estratégico',
    'Treinamento completo personalizado da equipe',
    'Relatórios inteligentes sob medida',
    'Integração robusta com ERPs',
    'Backup automático e seguro de dados'
  ];

  return (
    <div className="min-h-screen pt-18 relative">
      {/* Scroll Progress Indicator */}
      <ScrollProgress />
      
      {/* Floating CTA */}
      <FloatingCTA onScroll={scrollToForm} />



      {/* Hero Section with Animated Background */}
      <section 
        id="hero" 
        data-animate
        className="relative min-h-[85vh] flex items-center justify-center overflow-hidden"
      >
        {/* Animated Background */}
        <AnimatedBackground variant="hero" />
        
        {/* Traditional Background Overlay for Fallback */}
        <div className="absolute inset-0">
          <img 
            src="images/event-background.jpeg"
            alt="Grande evento lotado"
            className="w-full h-full object-cover opacity-30"
            style={{ transform: `translateY(${scrollY * 0.3}px)` }}
          />
          
          {/* Enhanced overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-black-deep/80 via-black-deep/60 to-black-deep/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black-deep/90 via-transparent to-transparent"></div>
        </div>

        {/* Content with Enhanced Animations */}
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl">
            <div className="space-y-6 text-white">
              {/* Enhanced Navigation */}
              <div className="flex items-center justify-between mb-8">
                <Button
                  variant="ghost"
                  onClick={() => onNavigate?.('home')}
                  className="text-white/70 hover:text-white transition-all duration-300 hover:translate-x-1 group backdrop-blur-sm bg-white/5 rounded-lg"
                >
                  <ArrowLeft size={20} className="mr-2 transition-transform duration-300 group-hover:-translate-x-1" />
                  Voltar
                </Button>
              </div>

              {/* Kicker with Animation */}
              <div 
                className={`flex items-center space-x-3 transition-all duration-700 ${
                  isInView.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: '200ms' }}
              >
                <div className="w-12 h-[2px] bg-orange-primary"></div>
                <p className="text-orange-primary font-medium tracking-wide uppercase text-sm">
                  Muito além dos ingressos
                </p>
              </div>

              {/* Main Headline with Staggered Animation */}
              <h1 
                className={`font-display text-5xl md:text-7xl leading-tight transition-all duration-700 ${
                  isInView.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: '400ms' }}
              >
                A tecnologia por trás dos{' '}
                <span className="text-orange-primary">maiores eventos</span>
              </h1>

              {/* Subheadline */}
              <p 
                className={`text-xl md:text-2xl text-white/90 max-w-3xl leading-relaxed transition-all duration-700 ${
                  isInView.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: '600ms' }}
              >
                Plataforma completa de ingressos, inteligência e controle de acesso – 
                feita sob medida para grandes produtoras.
              </p>

              {/* Enhanced CTA - Single Button */}
              <div 
                className={`flex justify-center pt-8 transition-all duration-700 ${
                  isInView.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: '800ms' }}
              >
                <Button
                  onClick={redirectToRegistration}
                  size="lg"
                  className="bg-gradient-to-r from-orange-primary to-orange-primary/80 hover:from-orange-primary/90 hover:to-orange-primary/70 text-white px-8 py-4 text-lg font-semibold shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-orange-primary/25 group"
                >
                  <Plus size={20} className="mr-2 group-hover:rotate-90 transition-transform duration-300" />
                  Cadastrar evento
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </div>

              {/* Trust Indicators with Enhanced Animation */}
              <div 
                className={`pt-12 transition-all duration-700 ${
                  isInView.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: '1000ms' }}
              >
                {/* Mobile: Clean Vertical Stack */}
                <div className="flex flex-col space-y-6 sm:hidden">
                  {[
                    { icon: CheckCircle, text: 'Integração completa entre canais' },
                    { icon: Shield, text: 'Tecnologia bancária segura' },
                    { icon: Target, text: 'Feito para grandes eventos' },
                    { icon: Headphones, text: 'Suporte especializado 24/7' }
                  ].map((item, index) => (
                    <div 
                      key={index}
                      className="group flex items-center space-x-4 transition-all duration-500 hover:translate-x-2"
                      style={{ transitionDelay: `${1200 + index * 100}ms` }}
                    >
                      <item.icon size={20} className="text-green-400 transition-transform duration-300 group-hover:scale-110 flex-shrink-0" />
                      <span className="text-white/90 font-medium tracking-wide">
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Desktop: Aligned with text content */}
                <div className="hidden sm:flex items-center">
                  <div className="flex flex-wrap items-center gap-x-8 gap-y-4 lg:gap-x-12">
                    {[
                      { icon: CheckCircle, text: 'Integração completa entre canais' },
                      { icon: Shield, text: 'Tecnologia bancária segura' },
                      { icon: Target, text: 'Feito para grandes eventos' },
                      { icon: Headphones, text: 'Suporte especializado 24/7' }
                    ].map((item, index) => (
                      <div 
                        key={index}
                        className="group flex items-center space-x-3 transition-all duration-500 hover:scale-105"
                        style={{ transitionDelay: `${1200 + index * 100}ms` }}
                      >
                        <item.icon size={18} className="text-green-400 transition-transform duration-300 group-hover:rotate-12 flex-shrink-0" />
                        <span className="text-white/90 font-medium text-sm tracking-wide whitespace-nowrap">
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos Bar with Animated Background */}
      <section 
        id="partners" 
        data-animate
        className={`relative py-12 border-y border-border/50 transition-all duration-700 ${
          isInView.partners ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <AnimatedBackground variant="subtle" className="opacity-50" />
        <div className="relative z-10 container mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-muted-foreground font-medium text-sm tracking-wide uppercase mb-2">
              Integração completa com
            </p>
            <h2 className="text-2xl font-semibold text-foreground">
              Principais meios de pagamento
            </h2>
          </div>
          
          {/* Partner logos grid */}
          <div className="flex flex-wrap items-center justify-center gap-6 lg:gap-8">
            {partnerLogos.map((partner, index) => (
              <div 
                key={partner.name}
                className="transition-all duration-500"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <LogoContainer partner={partner} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section 
        id="features" 
        data-animate
        className={`py-20 transition-all duration-700 ${
          isInView.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-8 h-[2px] bg-orange-primary"></div>
              <p className="text-orange-primary font-medium tracking-wide uppercase text-sm">
                Tecnologia Completa
              </p>
              <div className="w-8 h-[2px] bg-orange-primary"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Tudo o que você precisa para{' '}
              <span className="text-orange-primary">grandes eventos</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Uma plataforma integrada com funcionalidades avançadas para gestão completa 
              de eventos corporativos e shows de grande porte.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className={`group cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 border-border/50 bg-card/50 backdrop-blur-sm ${
                  activeFeature === index ? 'ring-2 ring-orange-primary/30 shadow-xl' : ''
                }`}
                onClick={() => setActiveFeature(index)}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <CardHeader className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-primary/20 to-orange-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon size={32} className="text-orange-primary" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-foreground group-hover:text-orange-primary transition-colors">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-center text-muted-foreground leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detailed Feature Display */}
          <Card className="border-border/50 bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-sm shadow-2xl">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center mb-4">
                    {React.createElement(features[activeFeature].icon, { 
                      size: 32, 
                      className: "text-orange-primary mr-3" 
                    })}
                    <h3 className="text-2xl font-bold text-foreground">
                      {features[activeFeature].title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {features[activeFeature].description}
                  </p>
                  <ul className="space-y-3">
                    {features[activeFeature].details.map((detail, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle size={20} className="text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-foreground">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative">
                  <div className="aspect-video bg-gradient-to-br from-muted/50 to-muted/20 rounded-lg flex items-center justify-center border border-border/50">
                    {React.createElement(features[activeFeature].icon, { 
                      size: 80, 
                      className: "text-orange-primary/30" 
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Physical Sales Section */}
      <section 
        id="physical-sales" 
        data-animate
        className={`py-20 bg-muted/30 transition-all duration-700 ${
          isInView['physical-sales'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-8 h-[2px] bg-orange-primary"></div>
              <p className="text-orange-primary font-medium tracking-wide uppercase text-sm">
                Tecnologia Avançada
              </p>
              <div className="w-8 h-[2px] bg-orange-primary"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Tecnologia avançada{' '}
              <span className="text-orange-primary">integrada e inteligente</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Venda direta na maquininha, proteção antifraude com IA e tecnologia de conversão 
              que maximiza seus resultados com estoque unificado.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {physicalSalesBenefits.map((benefit, index) => (
              <Card 
                key={index}
                className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-border/50 bg-card/50 backdrop-blur-sm"
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <CardHeader>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-primary/20 to-orange-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <benefit.icon size={32} className="text-orange-primary" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground group-hover:text-orange-primary transition-colors">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <Button 
              onClick={redirectToRegistration}
              size="lg"
              className="bg-gradient-to-r from-orange-primary to-orange-primary/80 hover:from-orange-primary/90 hover:to-orange-primary/70 text-white px-8 py-4 text-lg font-semibold shadow-2xl transition-all duration-300 hover:scale-105 group"
            >
              <Zap size={20} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
              Quero essas tecnologias
              <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section 
        id="benefits" 
        data-animate
        className={`py-20 bg-muted/30 transition-all duration-700 ${
          isInView.benefits ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-[2px] bg-orange-primary"></div>
                <p className="text-orange-primary font-medium tracking-wide uppercase text-sm">
                  Suporte Premium
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Muito além de{' '}
                <span className="text-orange-primary">uma plataforma</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Oferecemos uma parceria estratégica completa para o sucesso dos seus eventos, 
                com suporte dedicado e implementação personalizada.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-center space-x-3 transition-all duration-300 hover:translate-x-2"
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
                    <span className="text-foreground font-medium">{benefit}</span>
                  </div>
                ))}
              </div>

              <Button 
                onClick={redirectToWhatsApp}
                size="lg"
                className="bg-gradient-to-r from-orange-primary to-orange-primary/80 hover:from-orange-primary/90 hover:to-orange-primary/70 text-white px-8 py-4 text-lg font-semibold shadow-2xl transition-all duration-300 hover:scale-105 group"
              >
                <MessageCircle size={20} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                Falar com especialista
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </div>

            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-orange-primary/20 via-transparent to-orange-primary/10 rounded-3xl flex items-center justify-center border border-border/50">
                <div className="text-center">
                  <Building size={120} className="text-orange-primary/30 mx-auto mb-4" />
                  <p className="text-2xl font-bold text-foreground">
                    Partnership Estratégico
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section 
        ref={formRef}
        id="contact" 
        data-animate
        className={`py-20 bg-muted/30 transition-all duration-700 ${
          isInView.contact ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-8 h-[2px] bg-orange-primary"></div>
              <p className="text-orange-primary font-medium tracking-wide uppercase text-sm">
                Vamos Começar
              </p>
              <div className="w-8 h-[2px] bg-orange-primary"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Pronto para o{' '}
              <span className="text-orange-primary">próximo nível?</span>
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Fale diretamente com nossa equipe especializada pelo WhatsApp 
              ou cadastre seu evento agora mesmo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* WhatsApp Contact Card */}
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-2xl">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                  <MessageCircle size={40} className="text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  Fale com um especialista
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Nossa equipe está pronta para entender suas necessidades e criar 
                  uma solução personalizada para seu evento.
                </p>
                <Button
                  onClick={redirectToWhatsApp}
                  size="lg"
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold shadow-2xl transition-all duration-300 hover:scale-105 group"
                >
                  <MessageCircle size={20} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                  Conversar no WhatsApp
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
                <div className="flex items-center justify-center space-x-2 mt-4 text-sm text-muted-foreground">
                  <Clock size={16} className="text-green-500" />
                  <span>Resposta imediata</span>
                </div>
              </CardContent>
            </Card>

            {/* Event Registration Card */}
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm shadow-2xl">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-orange-primary/20 flex items-center justify-center mx-auto mb-6">
                  <Plus size={40} className="text-orange-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  Cadastrar evento
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Comece agora mesmo! Cadastre seu evento em nossa plataforma 
                  e tenha acesso a todas as funcionalidades.
                </p>
                <Button
                  onClick={redirectToRegistration}
                  size="lg"
                  className="w-full bg-gradient-to-r from-orange-primary to-orange-primary/80 hover:from-orange-primary/90 hover:to-orange-primary/70 text-white font-semibold shadow-2xl transition-all duration-300 hover:scale-105 group"
                >
                  <Plus size={20} className="mr-2 group-hover:rotate-90 transition-transform duration-300" />
                  Cadastrar agora
                  <Sparkles size={16} className="ml-2 group-hover:scale-110 transition-transform duration-300" />
                </Button>
                <div className="flex items-center justify-center space-x-4 mt-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <Shield size={16} className="text-green-500" />
                    <span>100% seguro</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Star size={16} className="text-orange-primary" />
                    <span>Gratuito</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}